import { delay, put, select, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';

import { fetchKnmComplete336ById } from 'APIPackage/KnmModule/services/knmActionsApi';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { onApprovalSelectOption } from 'KnmErknmPackage/MainModule/constants/approvedSelectOptions';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import {
  getKnmData,
  getKnmDataCreated,
  getKnmFormRef,
  getKnmIsErknmStatusReadyForApprove,
  getKnmOnSaveButtonClick,
  getKnmSystemType,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { KnmSystemTypesEnum } from 'KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { REFRESH_DELAY } from 'CorePackage/MainModule/constants/refreshDelay';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { KNM_COMPLETE336_SUCCESS, KNM_COMPLETE336_VALIDATION_ERROR } from 'CorePackage/MainModule/constants/notices';
import { BAD_REQUEST, SERVER_ERROR } from 'CorePackage/MainModule/constants/httpErrors';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { IKnmApprove248 } from 'APIPackage/KnmModule/interfaces/IKnmApprove248';

import { knmAddVisibleModal, knmComplete336Success } from '../../MainModule/actions/knmActions';
import { KnmHeaderActionsEnum } from '../enums/KnmHeaderActionsEnum';
import { knmComplete336 } from '../actions/knmHeaderActions';
import { getPlanListPlan } from '../../../PlanListPackage/MainModule/selectors/planListSelectors';
import { FnsCheckStatusesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/enums/FnsCheckStatusesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';

export function* watchKnmHeaderSaga() {
  yield takeEvery(KnmHeaderActionsEnum.KNM_COMPLETE336, knmComplete336Saga);
  yield takeEvery(KnmHeaderActionsEnum.KNM_HEADER_HANDLE_TO_APPROVE_CLICK, knmHeaderHandleToApproveClickSaga);
}

function* knmComplete336Saga(action: ActionType<typeof knmComplete336>) {
  function* funcTry() {
    const knmResponse: IKnm = yield fetchKnmComplete336ById(action.payload.id);

    yield delay(REFRESH_DELAY);
    yield put(knmComplete336Success(knmResponse));
    yield put(
      addNoticeAction({
        renderContent: KNM_COMPLETE336_SUCCESS,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.KNM_COMPLETE_336,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: error?.message?.includes(BAD_REQUEST) ? KNM_COMPLETE336_VALIDATION_ERROR : SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_COMPLETE_336,
        }),
      );
    },
  });
}

function* knmHeaderHandleToApproveClickSaga() {
  const state: IRootState = yield select();
  const knmSystemType = getKnmSystemType(state);
  const isErp = knmSystemType === KnmSystemTypesEnum.ERP;
  const isErknm = knmSystemType === KnmSystemTypesEnum.ERKNM;
  const knmData = getKnmData(state);
  const formRef = getKnmFormRef(state);
  const onSaveButtonClick = getKnmOnSaveButtonClick(state);
  const planData = getPlanListPlan(state);

  const settings = getAdminFetchSettings(state);
  const created = getKnmDataCreated(state);
  const isShowCheckResultMessage = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.SHOW_CHECK_RESULT_MESSAGE_523_4_1_17,
  );
  const isKnmStatusReadyForApprove = getKnmIsErknmStatusReadyForApprove(state);

  const isWaitEgripEgrulResult = knmData?.knmErknm?.organizations?.some(item => {
    if (item.ervkId || !isShowCheckResultMessage) {
      return false;
    }

    const isUl = item.subjectType?.code === 'UL';
    const isIp = item.subjectType?.code === 'IP';
    const egrulStatus = item.checkSubjectResult?.egrul?.status;
    const egripStatus = item.checkSubjectResult?.egrip?.status;

    return (
      (isUl || isIp) &&
      egrulStatus !== FnsCheckStatusesEnum.FOUND &&
      egripStatus !== FnsCheckStatusesEnum.FOUND &&
      (!item.organizationName || !item.firstOrganizationName)
    );
  });

  const isWaitMspResult = knmData?.knmErknm?.organizations?.some(item => {
    if (item.ervkId || !isShowCheckResultMessage) {
      return false;
    }

    const isUl = item.subjectType?.code === 'UL';
    const isIp = item.subjectType?.code === 'IP';
    const mspStatus = item.checkSubjectResult?.msp?.status;

    return (isUl || isIp) && mspStatus !== FnsCheckStatusesEnum.FOUND && mspStatus !== FnsCheckStatusesEnum.NO_DATA_MSP;
  });

  if (isErp) {
    yield put(knmAddVisibleModal(KnmModalsEnum.CHANGE_STATUS_MODAL_TO_APPROVE));
  }
  if (isErknm) {
    if (!knmData?.signed) {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.APPROVED_KNM,
          renderContent: 'Для отправки на согласование необходимо подписать паспорт',
        }),
      );
    } else if (planData && !planData.signed) {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.APPROVED_KNM,
          renderContent: 'Для отправки КНМ на согласование необходимо подписать план',
        }),
      );
    } else if (isWaitEgripEgrulResult || isWaitMspResult) {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.APPROVED_KNM,
          renderContent: 'Для отправки КНМ на согласование необходимо дождаться обновления данных из выписки ФНС',
        }),
      );
    } else {
      if (needForChange(knmData, isKnmStatusReadyForApprove)) {
        formRef?.current.change('approved', onApprovalSelectOption);
      }

      onSaveButtonClick?.();
    }
  }
}

const createEmptyApprove = (approved: ApprovedTypesEnum): IKnmApprove248 => ({
  approved,
  approveRequired: null,
  approveNote: null,
  dateDecision: null,
  numberDecision: null,
  fioSigner: null,
  titleSigner: null,
  noteAppeal: null,
  rejectReasonType: null,
  rejectReasonNote: null,
  dateRejectReason: null,
  prosecutorRegStatus: null,
  note: null,
  appeal: null,
  status: null,
  approveFile: null,
  isNeedApplyChangesInApprovedPlan: null,
  remarks: null,
  wasRejected: null,
});

const needForChange = (knmData: IKnm, isKnmStatusReadyForApprove: boolean) => {
  if (
    knmData.knmErknm?.approve == null ||
    (knmData.knmErknm.approve.approved !== ApprovedTypesEnum.APPROVED &&
      knmData.knmErknm.approve.approved !== ApprovedTypesEnum.APPROVED_WITH_REMARK)
  ) {
    return true;
  }

  const changePlanReasons = knmData.knmErknm.changePlanReason;

  if (changePlanReasons && changePlanReasons.length !== 0) {
    const sortedReasons = changePlanReasons.reduce((latest, current) => {
      return new Date(current.dateCreate!) > new Date(latest.dateCreate!) ? current : latest;
    });

    if (sortedReasons.reasonChangeType?.code === 'RSN_CHNG_PP_XXII') {
      if (sortedReasons.approve == null || sortedReasons.approve.approved == null) {
        if (isKnmStatusReadyForApprove) {
          sortedReasons.approve = createEmptyApprove(ApprovedTypesEnum.ON_APPROVAL);
        }

        return false;
      }
    }
  }

  return true;
};
